import { useState } from "react";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../context/AuthContext";

type SubCategory = {
  id: string;
  parentCategory: string;
  name: string;
  slug: string;
  description: string;
  status: "Active" | "Hidden";
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  createdAt: string;
};

const sampleCategories = ["Necklaces","Earrings","Bangles","Rings","Anklets","Maang Tikka"];

export function CreateSubCategoryPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";

  const [parentCategory, setParentCategory] = useState("");
  const [name, setName]                     = useState("");
  const [slug, setSlug]                     = useState("");
  const [description, setDescription]       = useState("");
  const [status, setStatus]                 = useState<"Active" | "Hidden">("Active");
  const [metaTitle, setMetaTitle]           = useState("");
  const [metaDesc, setMetaDesc]             = useState("");
  const [metaKeys, setMetaKeys]             = useState("");
  const [canonicalUrl, setCanonicalUrl]     = useState("");
  const [ogTitle, setOgTitle]               = useState("");
  const [ogDesc, setOgDesc]                 = useState("");
  const [ogImage, setOgImage]               = useState("");
  const [saved, setSaved]                   = useState(false);
  const [subCategories, setSubCategories]   = useState<SubCategory[]>([]);

  const [editingId, setEditingId]     = useState<string | null>(null);
  const [editForm, setEditForm]       = useState<Partial<SubCategory>>({});

  function handleNameChange(val: string) {
    setName(val);
    setSlug(val.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
  }

  function handleSubmit() {
    if (!name.trim() || !parentCategory) return;
    setSubCategories(prev => [{
      id: `sub-${Date.now()}`,
      parentCategory, name: name.trim(), slug, description, status,
      metaTitle: metaTitle || `${name.trim()} ${parentCategory} | NineSisters`,
      metaDescription: metaDesc,
      metaKeywords: metaKeys,
      canonicalUrl,
      ogTitle: ogTitle || metaTitle || name.trim(),
      ogDescription: ogDesc || metaDesc,
      ogImage,
      createdAt: new Date().toLocaleDateString("en-IN"),
    }, ...prev]);
    setName(""); setSlug(""); setDescription(""); setParentCategory(""); setStatus("Active");
    setMetaTitle(""); setMetaDesc(""); setMetaKeys(""); setCanonicalUrl("");
    setOgTitle(""); setOgDesc(""); setOgImage("");
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  function deleteItem(id: string) { setSubCategories(prev => prev.filter(s => s.id !== id)); }
  function startEdit(s: SubCategory) { setEditingId(s.id); setEditForm({ ...s }); }
  function saveEdit(id: string) { setSubCategories(prev => prev.map(s => s.id === id ? { ...s, ...editForm } as SubCategory : s)); setEditingId(null); setEditForm({}); }
  function cancelEdit() { setEditingId(null); setEditForm({}); }
  function toggleStatus(id: string) {
    setSubCategories(prev => prev.map(s => s.id === id ? { ...s, status: s.status === "Active" ? "Hidden" : "Active" } : s));
  }

  return (
    <div className="d-grid gap-4">
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-4">
            <div className="mst-icon-box"><i className="bi bi-diagram-3"></i></div>
            <div>
              <h5 className="mb-0 fw-bold mst-title">Create Sub Category</h5>
              <small className="mst-subtitle">Add a sub-category under an existing category</small>
            </div>
          </div>

          {saved && (
            <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3 mst-alert">
              <i className="bi bi-check-circle-fill"></i> Sub Category saved successfully!
            </div>
          )}

          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Parent Category <span className="text-danger">*</span></label>
              <select className="form-select mst-select" value={parentCategory}
                onChange={e => setParentCategory(e.target.value)}>
                <option value="">Select parent category...</option>
                {sampleCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Sub Category Name <span className="text-danger">*</span></label>
              <input className="form-control mst-control" placeholder="e.g. Choker Necklaces"
                value={name} onChange={e => handleNameChange(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Slug (auto-generated)</label>
              <div className="input-group">
                <span className="input-group-text mst-ig-text">/sub/</span>
                <input className="form-control mst-control-right" placeholder="choker-necklaces"
                  value={slug} onChange={e => setSlug(e.target.value)} />
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Status</label>
              <select className="form-select mst-select" value={status}
                onChange={e => setStatus(e.target.value as "Active" | "Hidden")}>
                <option value="Active">Active</option>
                <option value="Hidden">Hidden</option>
              </select>
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Description</label>
              <textarea className="form-control mst-textarea" placeholder="About this sub-category..." rows={3}
                value={description} onChange={e => setDescription(e.target.value)} />
            </div>

            <div className="col-12 mt-2">
              <div className="mst-seo-divider"><i className="bi bi-search me-2"></i>SEO & Meta Settings</div>
            </div>
            <div className="col-12">
              <div className="mst-seo-hint"><i className="bi bi-info-circle me-1"></i>These fields control how this sub-category appears in Google search results and social media.</div>
            </div>
            <div className="col-md-8">
              <label className="form-label fw-semibold mst-label">Meta Title <span className="mst-seo-counter">{metaTitle.length}/60</span></label>
              <input className="form-control mst-control" placeholder="e.g. Choker Necklaces | NineSisters Jewels" maxLength={60}
                value={metaTitle} onChange={e => setMetaTitle(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Meta Keywords</label>
              <input className="form-control mst-control" placeholder="e.g. choker, necklace, gold"
                value={metaKeys} onChange={e => setMetaKeys(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Meta Description <span className="mst-seo-counter">{metaDesc.length}/160</span></label>
              <textarea className="form-control mst-textarea" placeholder="Brief summary shown in search results — 120–160 characters recommended" rows={2} maxLength={160}
                value={metaDesc} onChange={e => setMetaDesc(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Canonical URL <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="https://ninesistersjewels.com/sub/..."
                value={canonicalUrl} onChange={e => setCanonicalUrl(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Image URL <span className="mst-seo-opt">(Social share image)</span></label>
              <input className="form-control mst-control" placeholder="https://..."
                value={ogImage} onChange={e => setOgImage(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Title <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="Social media share title"
                value={ogTitle} onChange={e => setOgTitle(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Description <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="Social media preview description"
                value={ogDesc} onChange={e => setOgDesc(e.target.value)} />
            </div>
          </div>

          <div className="d-flex gap-2 mt-4">
              <button type="button" className="btn btn-primary px-4 mst-btn-save"
                onClick={handleSubmit} disabled={!name.trim() || !parentCategory}>
                <i className="bi bi-plus-circle me-2"></i>Save Sub Category
              </button>
              <button type="button" className="btn mst-btn-reset"
                onClick={() => { setName(""); setSlug(""); setDescription(""); setParentCategory(""); setStatus("Active"); setMetaTitle(""); setMetaDesc(""); setMetaKeys(""); setCanonicalUrl(""); setOgTitle(""); setOgDesc(""); setOgImage(""); }}>
                Reset
              </button>
            </div>
        </div>
      </div>

      <DataTable<SubCategory>
        title="Sub Categories"
        rows={subCategories}
        rowKey={r => r.id}
        searchPlaceholder="Search sub-categories..."
        exportFileName="sub-categories"
        columns={[
          { key: "name", label: "Sub Category", sortable: true,
            render: r => editingId === r.id
              ? <input className="form-control form-control-sm" value={editForm.name ?? ""} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              : <span className="fw-semibold">{r.name}</span>
          },
          { key: "parentCategory", label: "Parent",
            render: r => <span className="badge mst-cat-badge">{r.parentCategory}</span> },
          { key: "slug", label: "Slug", render: r => <code className="mst-code">{r.slug}</code> },
          { key: "description", label: "Description",
            render: r => editingId === r.id
              ? <input className="form-control form-control-sm" value={editForm.description ?? ""} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
              : <span className="text-muted mst-desc">{r.description || "—"}</span>
          },
          { key: "status", label: "Status",
            render: r => <span className={`badge ${r.status === "Active" ? "text-bg-success" : "text-bg-secondary"}`}>{r.status}</span>
          },
          { key: "createdAt", label: "Created", sortable: true },
          ...(canManage ? [{
            key: "actions" as const, label: "Actions", className: "text-end",
            render: (r: SubCategory) => editingId === r.id ? (
              <div className="pg-actions">
                <button type="button" className="btn btn-sm btn-success" onClick={() => saveEdit(r.id)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelEdit}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            ) : (
              <div className="pg-actions">
                <button type="button" className="btn btn-sm btn-outline-primary mst-btn-status" onClick={() => startEdit(r)} title="Edit">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary mst-btn-status" onClick={() => toggleStatus(r.id)}>
                  {r.status === "Active" ? "Hide" : "Show"}
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger mst-btn-delete" onClick={() => deleteItem(r.id)}>
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            ),
          }] : []),
        ]}
      />
    </div>
  );
}
